// Оружие на клиенте — единая точка входа для main.ts: ввод (1–9, колесо,
// Q, ПКМ-приближение, тач-кнопка), инвентарь и предсказание спуска, HUD,
// вид от первого лица, оружие в руках у других, снаряды, эффекты и звук.
//
// main.ts вызывает: fixedTick (спуск), selectBits (выбор → Input), onSnapshot,
// onEvent / onShot / onNpcShot (события), frame (кадр до draw), beforeRender
// (камера: вид от первого лица, тряска, приближение), appendLights.

import type { MeshInstance, PointLight, Renderer } from "../engine/renderer";
import type { ParticleSimulation } from "../engine/particles/simulate";
import type { Input } from "../engine/input";
import {
  EvType, ProjectileKind, ShooterKind, WeaponFlag,
  type GameEvent, type Snapshot, type SnapshotProjectile,
} from "../net/protocol";
import { WeaponId, weaponDef } from "./catalog";
import { t } from "../i18n";
import { WeaponState } from "./state";
import { WeaponHud } from "./hud";
import { WeaponAssets } from "./assets";
import { ViewModel } from "./viewmodel";
import { ThirdPersonWeapons } from "./thirdPerson";
import { WeaponEffects } from "./effects";
import { GLTF_TO_WORLD, m4, m4Basis, m4Mul } from "./mat";
import { WeaponPickups } from "./pickups";
import type { PickupInit } from "../net/protocol";

export interface WeaponsHost {
  renderer: Renderer;
  particles: ParticleSimulation;
  input: Input;
  myId(): number;
  soundVolume(): number;
  lite(): boolean;
  /** Вид от третьего лица (или мёртв). */
  thirdPerson(): boolean;
  /** Идёт игра и ввод разрешён (захвачен курсор / тач-режим). */
  inGame(): boolean;
  /** Свой игрок жив и управляем. */
  controlActive(): boolean;
  /** Ступни и скорость своего игрока (для покачивания оружия и звука). */
  localState(): { x: number; y: number; z: number; speed: number; grounded: boolean };
  playerInstances(): Map<number, MeshInstance>;
  npcInstances(): Map<number, MeshInstance>;
  /** Pickup'ы сцены (оружие на полу рисует и подсвечивает этот модуль). */
  pickups(): Map<number, PickupInit>;
  /** ?fpvideo=1 — старые видео-оверлеи вместо 3D-оружия (сравнение). */
  fpVideo: boolean;
  /** Чужой выстрел/удар (не пуля): анимация стрельбы у игрока/бота. */
  onRemoteFire(kind: number, id: number): void;
}

/** Снаряд на клиенте: экстраполяция между снапшотами. */
interface ProjVis {
  id: number;
  kind: number;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  at: number; // ms
  inst: MeshInstance | null;
  trailCarry: number;
  lastBounceVz: number;
}

/** Оптика снайперки: кратность колесом от ×3,6 (22°) до ×7,5 (10°). */
const ZOOM_FOV = 22 * Math.PI / 180;
const ZOOM_FOV_MIN = 10 * Math.PI / 180;
const BASE_FOV = 75 * Math.PI / 180;

export class WeaponsClient {
  readonly state = new WeaponState();
  readonly assets: WeaponAssets;
  readonly fx: WeaponEffects;
  private hud: WeaponHud;
  private vm: ViewModel;
  private tp: ThirdPersonWeapons;
  private floor: WeaponPickups;
  private zoomHeld = false;
  /** Оптика включена переключателем (Z / кнопка на телефоне), а не удержанием ПКМ. */
  private zoomToggled = false;
  private zoomFov = ZOOM_FOV;
  private zoom = 0;
  /** Оружие и флаги других игроков/ботов из снапшота. */
  private playerWeapon = new Map<number, number>();
  private playerFlags = new Map<number, number>();
  private npcWeapon = new Map<number, number>();
  private npcFlags = new Map<number, number>();
  private npcAlive = new Map<number, boolean>();
  private projectiles = new Map<number, ProjVis>();
  private fireHeldLocal = false;
  private lastFireLocalMs = 0;
  private autoSwitchAt = 0;
  private debugDummies: { inst: MeshInstance; weapon: number }[] = [];
  /** Своё дуло в мире (FP — от модели, TP — от оружия в руке). */
  private ownMuzzle: [number, number, number] | null = null;
  private remoteMuzzle = new Map<string, [number, number, number]>();

  constructor(private host: WeaponsHost) {
    this.assets = new WeaponAssets(host.renderer);
    this.fx = new WeaponEffects(
      host.renderer, host.particles,
      () => host.localState(),
      () => host.soundVolume(),
      () => host.lite(),
    );
    this.hud = new WeaponHud(
      (id) => this.select(id),
      () => { if (this.state.cycle(1, performance.now())) this.onSelected(); },
      () => this.toggleScope(),
    );
    this.vm = new ViewModel(host.renderer, this.assets);
    this.tp = new ThirdPersonWeapons(host.renderer, this.assets);
    this.floor = new WeaponPickups(host.renderer, host.particles, this.assets);
    void this.assets.preload();
    this.installInput();
    // ?wdebug=1 — ручки для автотестов/скриншотов (выбор оружия, спуск).
    if (new URLSearchParams(location.search).get("wdebug") === "1") {
      (window as unknown as Record<string, unknown>).__weapons = {
        client: this,
        input: host.input,
        select: (id: number) => this.select(id),
        status: () => ({
          myId: host.myId(), inGame: host.inGame(), control: host.controlActive(), tp: host.thirdPerson(),
          active: this.state.active, owned: this.state.owned, ammo: this.state.ammo, local: host.localState(),
          pickups: [...host.pickups().values()].map((p) => [p.id, p.type, p.state, +p.x.toFixed(1), +p.y.toFixed(1), +p.z.toFixed(2)]),
        }),
        aimAtNpc: () => {
          const l = host.localState();
          let best: MeshInstance | null = null;
          let bd = 1e9;
          for (const inst of host.npcInstances().values()) {
            const d = Math.hypot(inst.x - l.x, inst.y - l.y);
            if (d < bd) { bd = d; best = inst; }
          }
          if (!best) return null;
          const dx = best.x - l.x, dy = best.y - l.y, dz = best.z + 1.0 - (l.z + 0.2);
          host.input.yaw = Math.atan2(dy, dx);
          host.input.pitch = Math.atan2(dz, Math.hypot(dx, dy));
          return { d: bd, x: best.x, y: best.y };
        },
        /** Манекен-солдат с оружием перед игроком (проверка третьего лица). */
        dummy: (weapon: number, dist = 2.6, side = 0, turn = Math.PI) => {
          const me = host.playerInstances().get(host.myId());
          if (!me) return null;
          const l = host.localState();
          const yaw = host.input.yaw;
          const inst = host.renderer.createMeshInstance({
            mesh: me.mesh, innerMesh: me.innerMesh, innerBodyVoxShaderCarve: false,
            x: l.x + Math.cos(yaw) * dist - Math.sin(yaw) * side,
            y: l.y + Math.sin(yaw) * dist + Math.cos(yaw) * side,
            z: me.z, yaw: yaw + turn, yawOffset: me.yawOffset, scale: me.scale, initialAnim: "idle",
          });
          host.renderer.addMeshInstance(inst);
          this.debugDummies.push({ inst, weapon });
          return this.debugDummies.length;
        },
        /** Положить «предмет» перед игроком (проверка подсветки подборов). */
        fakePickup: (type: number, dist = 2.2, side = 0) => {
          const l = host.localState();
          const yaw = host.input.yaw;
          const id = 60000 + Math.floor(Math.random() * 5000);
          const x = l.x + Math.cos(yaw) * dist - Math.sin(yaw) * side;
          const y = l.y + Math.sin(yaw) * dist + Math.cos(yaw) * side;
          const z = l.z - 1.6 + 0.35;
          host.pickups().set(id, {
            id, type, state: 0, amount: 0, yaw: 0, x, y, z,
            artifact: 0, fromX: x, fromY: y, fromZ: z, // artifacts
          });
          return id;
        },
        holdFire: (ms: number) => {
          host.input.buttons |= 1 << 4;
          setTimeout(() => { host.input.buttons &= ~(1 << 4); }, ms);
        },
      };
    }
  }

  // ── ввод ─────────────────────────────────────────────────────────────

  private installInput(): void {
    window.addEventListener("keydown", (e) => {
      if (!this.host.inGame() || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      const m = /^(?:Digit|Numpad)([1-9])$/.exec(e.code);
      if (m) {
        this.select(Number(m[1]) - 1);
        return;
      }
      if (e.code === "KeyQ" && !e.repeat) {
        if (this.state.selectPrevious(performance.now())) this.onSelected();
      }
      if (e.code === "KeyZ" && !e.repeat) this.toggleScope();
    });
    window.addEventListener("wheel", (e) => {
      if (!this.host.inGame() || document.pointerLockElement === null) return;
      if (Math.abs(e.deltaY) < 1) return;
      // В прицеле колесо меняет кратность, а не оружие.
      if (this.zoom > 0.5) {
        const k = e.deltaY > 0 ? 1.15 : 1 / 1.15;
        this.zoomFov = Math.max(ZOOM_FOV_MIN, Math.min(ZOOM_FOV, this.zoomFov * k));
        return;
      }
      if (this.state.cycle(e.deltaY > 0 ? 1 : -1, performance.now())) this.onSelected();
    }, { passive: true });
    window.addEventListener("mousedown", (e) => {
      if (e.button === 2 && document.pointerLockElement !== null) this.zoomHeld = true;
    });
    window.addEventListener("mouseup", (e) => {
      if (e.button === 2) this.zoomHeld = false;
    });
    window.addEventListener("contextmenu", (e) => {
      if (document.pointerLockElement !== null) e.preventDefault();
    });
  }

  select(id: number): void {
    if (this.state.select(id, performance.now())) this.onSelected();
  }

  /** Z / кнопка на телефоне: оптика вкл/выкл (только со снайперкой в руках). */
  private toggleScope(): void {
    if (this.state.active !== WeaponId.Sniper) return;
    this.zoomToggled = !this.zoomToggled;
  }

  private onSelected(): void {
    this.vm.setWeapon(this.state.active);
    this.fx.play2d("/sounds/weapons/switch.wav", 0.35);
  }

  /** Биты выбора оружия для Input.buttons. */
  selectBits(): number {
    return this.state.selectBits();
  }

  // ── спуск (фикс-тик) ─────────────────────────────────────────────────

  /** Предсказание спуска; true — «выстрел» (для анимации солдата). */
  fixedTick(fireHeld: boolean, firePrev: boolean, active: boolean): boolean {
    const now = performance.now();
    this.fireHeldLocal = active && fireHeld;
    if (!active) {
      this.state.tickFire(false, false);
      return false;
    }
    const r = this.state.tickFire(fireHeld, firePrev);
    if (r === "dry") {
      this.fx.play2d("/sounds/outOfAmmo.wav", 0.5);
      if (this.autoSwitchAt === 0) this.autoSwitchAt = now + 350;
      return false;
    }
    if (r !== "fire") return false;
    this.lastFireLocalMs = now;
    const w = this.state.active;
    const def = weaponDef(w);
    this.vm.onFire();
    // Звук своего выстрела — сразу (без ожидания сервера).
    if (w === WeaponId.Chainsaw || w === WeaponId.Flamethrower) {
      this.fx.keepLoop("self", def.sound, def.soundVol * this.host.soundVolume());
    } else if (w === WeaponId.Fists) {
      this.fx.play2d("/sounds/weapons/swing.wav", 0.5);
    } else {
      this.fx.play2d(def.sound, def.soundVol, def.soundRate);
    }
    // Вспышка/трассер/пламя от своего дула.
    const m = this.ownMuzzle;
    if (m) {
      const d = this.aimDir();
      this.fx.muzzle(w, m[0], m[1], m[2], d[0], d[1], d[2], !this.host.thirdPerson());
      if (w === WeaponId.Rifle || w === WeaponId.Sniper) {
        const L = 14;
        this.host.renderer.spawnTracer(m[0], m[1], m[2], m[0] + d[0] * L, m[1] + d[1] * L, m[2] + d[2] * L,
          w === WeaponId.Sniper ? { color: [0.9, 0.95, 1.0], size: 0.016, life: 0.25, density: 5, skip: 0.8 } : undefined);
      }
      if (w === WeaponId.Flamethrower) this.fx.flameJet(m[0], m[1], m[2], d[0], d[1], d[2], "self");
    }
    return true;
  }

  private aimDir(): [number, number, number] {
    const inp = this.host.input;
    const cy = Math.cos(inp.yaw), sy = Math.sin(inp.yaw);
    const cp = Math.cos(inp.pitch), sp = Math.sin(inp.pitch);
    return [cy * cp, sy * cp, sp];
  }

  // ── снапшот и события ────────────────────────────────────────────────

  onSnapshot(snap: Snapshot, myId: number): void {
    const now = performance.now();
    for (const p of snap.players) {
      this.playerWeapon.set(p.id, p.weapon);
      this.playerFlags.set(p.id, p.weaponFlags);
      if (p.id === myId) {
        const prevActive = this.state.active;
        const prevAmmo = this.state.ammoOf(prevActive);
        this.state.onSnapshot(snap.yourOwned, snap.yourAmmo, p.weapon, now);
        if (this.state.active !== prevActive) this.vm.setWeapon(this.state.active);
        // Патронов в руках стало больше (подбор) — «перезарядка».
        const a = this.state.ammoOf(this.state.active);
        if (this.state.active === prevActive && prevAmmo >= 0 && a > prevAmmo + 1) this.vm.onReload();
      }
    }
    const seen = new Set<number>();
    for (const n of snap.npcs) {
      this.npcWeapon.set(n.id, n.weapon);
      this.npcFlags.set(n.id, n.weaponFlags);
      this.npcAlive.set(n.id, n.state === 0 && n.hp > 0);
      seen.add(n.id);
    }
    for (const id of [...this.npcWeapon.keys()]) {
      if (!seen.has(id)) { this.npcWeapon.delete(id); this.npcFlags.delete(id); this.npcAlive.delete(id); }
    }
    this.syncProjectiles(snap.projectiles, now);
  }

  /** Своё/чужое попадание пули (EvShot): трассер по виду оружия. */
  onShot(ev: Extract<GameEvent, { type: typeof EvType.Shot }>, myId: number): void {
    const own = ev.shooterId === myId;
    let ox = ev.ox, oy = ev.oy, oz = ev.oz;
    const m = own ? this.ownMuzzle : this.remoteMuzzle.get(`p${ev.shooterId}`);
    if (m) { ox = m[0]; oy = m[1]; oz = m[2]; }
    if (own && (ev.weapon === WeaponId.Rifle || ev.weapon === WeaponId.Sniper)) return; // свой трассер уже нарисован
    this.fx.tracer(ev.weapon, ox, oy, oz, ev.hx, ev.hy, ev.hz, own);
  }

  /** Выстрел бота (EvNpcShot). true — обработано здесь (не автомат). */
  onNpcShot(ev: Extract<GameEvent, { type: typeof EvType.NpcShot }>): boolean {
    if (ev.weapon === WeaponId.Rifle) return false;
    const m = this.remoteMuzzle.get(`n${ev.npcId}`);
    const ox = m ? m[0] : ev.ox, oy = m ? m[1] : ev.oy, oz = m ? m[2] : ev.oz;
    this.fx.tracer(ev.weapon, ox, oy, oz, ev.hx, ev.hy, ev.hz, false);
    return true;
  }

  /** События оружия (EvWeaponFire, EvExplosion). true — обработано. */
  onEvent(ev: GameEvent, myId: number): boolean {
    if (ev.type === EvType.WeaponFire) {
      const own = ev.shooterKind === ShooterKind.Player && ev.shooterId === myId;
      if (own) return true; // свой — предсказан в fixedTick
      this.host.onRemoteFire(ev.shooterKind, ev.shooterId);
      const key = (ev.shooterKind === ShooterKind.Npc ? "n" : "p") + ev.shooterId;
      const m = this.remoteMuzzle.get(key);
      const ox = m ? m[0] : ev.ox, oy = m ? m[1] : ev.oy, oz = m ? m[2] : ev.oz;
      const def = weaponDef(ev.weapon);
      if (ev.weapon === WeaponId.Flamethrower) {
        this.fx.flameJet(ox, oy, oz, ev.dx, ev.dy, ev.dz, key);
      } else if (ev.weapon === WeaponId.Chainsaw) {
        this.fx.keepLoop(key, def.sound, this.fx.distVol(ox, oy, oz, def.soundVol));
        this.fx.sawSparks(ox, oy, oz, ev.dx, ev.dy, ev.dz);
      } else {
        this.fx.muzzle(ev.weapon, ox, oy, oz, ev.dx, ev.dy, ev.dz);
        const url = ev.weapon === WeaponId.Fists ? "/sounds/weapons/swing.wav" : def.sound;
        this.fx.playAt(url, ox, oy, oz, def.soundVol, def.soundRate);
      }
      return true;
    }
    if (ev.type === EvType.Explosion) {
      this.fx.explosion(ev.x, ev.y, ev.z, ev.radius);
      return true;
    }
    if (ev.type === EvType.Pickup && ev.playerId === myId && WeaponPickups.isWeapon(ev.pickupType)) {
      // Подобрали оружие: звук (дальше событие обрабатывает main — state).
      this.fx.play2d("/sounds/weapons/pickup.wav", 0.6);
    }
    return false;
  }

  // ── снаряды ──────────────────────────────────────────────────────────

  private syncProjectiles(list: SnapshotProjectile[], now: number): void {
    const seen = new Set<number>();
    for (const p of list) {
      seen.add(p.id);
      let v = this.projectiles.get(p.id);
      if (!v) {
        v = { ...p, at: now, inst: null, trailCarry: 0, lastBounceVz: p.vz };
        this.projectiles.set(p.id, v);
        if (p.kind === ProjectileKind.Grenade && !this.isNearOwn(p.x, p.y)) {
          this.fx.playAt("/sounds/weapons/throw.wav", p.x, p.y, p.z, 0.4);
        }
        continue;
      }
      // Отскок гранаты: смена знака вертикальной скорости вверх.
      if (p.kind === ProjectileKind.Grenade && v.vz < -1 && p.vz > 0.3) {
        this.fx.playAt("/sounds/weapons/bounce.wav", p.x, p.y, p.z, 0.5);
      }
      v.x = p.x; v.y = p.y; v.z = p.z;
      v.vx = p.vx; v.vy = p.vy; v.vz = p.vz;
      v.at = now;
    }
    for (const [id, v] of this.projectiles) {
      if (!seen.has(id)) {
        if (v.inst) this.host.renderer.removeMeshInstance(v.inst);
        this.projectiles.delete(id);
      }
    }
  }

  private isNearOwn(x: number, y: number): boolean {
    const l = this.host.localState();
    return Math.hypot(x - l.x, y - l.y) < 1.2;
  }

  private drawProjectiles(now: number, dt: number): void {
    for (const v of this.projectiles.values()) {
      const key = v.kind === ProjectileKind.Rocket ? "rocket_proj" : "grenade";
      const mesh = this.assets.get(key);
      if (!mesh) continue;
      if (!v.inst) {
        v.inst = this.host.renderer.createMeshInstance({ mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle" });
        v.inst.worldMatrix = m4();
        this.host.renderer.addMeshInstance(v.inst);
      }
      const t = Math.min(0.1, (now - v.at) / 1000);
      const g = v.kind === ProjectileKind.Grenade ? 20 : 0;
      const x = v.x + v.vx * t, y = v.y + v.vy * t;
      const z = Math.max(v.z + v.vz * t - 0.5 * g * t * t, v.kind === ProjectileKind.Grenade ? v.z - 0.3 : -1e9);
      const sp = Math.hypot(v.vx, v.vy, v.vz);
      let fx = 1, fy = 0, fz = 0;
      if (sp > 0.2) { fx = v.vx / sp; fy = v.vy / sp; fz = v.vz / sp; }
      // Граната кувыркается; ракета смотрит по полёту.
      const spin = v.kind === ProjectileKind.Grenade ? now / 1000 * 9 * Math.min(1, sp / 4) : 0;
      let ux = 0, uy = 0, uz = 1;
      const d = ux * fx + uy * fy + uz * fz;
      ux -= fx * d; uy -= fy * d; uz -= fz * d;
      const ul = Math.hypot(ux, uy, uz) || 1;
      ux /= ul; uy /= ul; uz /= ul;
      const lx = uy * fz - uz * fy, ly = uz * fx - ux * fz, lz = ux * fy - uy * fx;
      const cs = Math.cos(spin), sn = Math.sin(spin);
      const F = [fx * cs + ux * sn, fy * cs + uy * sn, fz * cs + uz * sn];
      const U = [ux * cs - fx * sn, uy * cs - fy * sn, uz * cs - fz * sn];
      m4Basis(this.tmp, F, [lx, ly, lz], U, [x, y, z]);
      m4Mul(v.inst.worldMatrix!, this.tmp, GLTF_TO_WORLD);
      v.inst.worldAabbDirty = true;
      if (v.kind === ProjectileKind.Rocket) {
        v.inst.emissive = [0.25, 0.12, 0.04];
        v.trailCarry += dt * 40;
        while (v.trailCarry >= 1) {
          v.trailCarry -= 1;
          this.fx.rocketTrail(x - fx * 0.22, y - fy * 0.22, z - fz * 0.22);
        }
        this.fx.addLight(x - fx * 0.3, y - fy * 0.3, z - fz * 0.3, 3.5, [1.8, 0.9, 0.3], 40);
      }
    }
  }
  private tmp = m4();

  // ── кадр ─────────────────────────────────────────────────────────────

  /** До draw(): оружие в руках у всех, снаряды, эффекты, HUD. */
  frame(dtSec: number, nowMs: number): void {
    const host = this.host;
    const myId = host.myId();
    // Автосмена после сухого щелчка.
    if (this.autoSwitchAt !== 0 && nowMs >= this.autoSwitchAt) {
      this.autoSwitchAt = 0;
      if (!this.state.hasAmmo(this.state.active)) {
        const best = this.state.bestWithAmmo();
        if (best >= 0) this.select(best);
      }
    }
    // Третье лицо: игроки.
    const alive = new Set<MeshInstance>();
    this.remoteMuzzle.clear();
    for (const [id, inst] of host.playerInstances()) {
      alive.add(inst);
      const w = id === myId ? this.state.active : (this.playerWeapon.get(id) ?? WeaponId.Rifle);
      const m = this.tp.sync(inst, w, true);
      if (m) this.remoteMuzzle.set(`p${id}`, m);
      if (id === myId && host.thirdPerson()) this.ownMuzzle = m;
      const fl = this.playerFlags.get(id) ?? 0;
      if (fl & WeaponFlag.Burning) this.fx.burning(inst.x, inst.y, inst.z);
      if (id !== myId && (fl & WeaponFlag.Firing) && w === WeaponId.Chainsaw && m) {
        this.fx.keepLoop(`p${id}`, weaponDef(w).sound, this.fx.distVol(m[0], m[1], m[2], 0.5));
      }
    }
    // Боты.
    for (const [id, inst] of host.npcInstances()) {
      alive.add(inst);
      const w = this.npcWeapon.get(id) ?? WeaponId.Rifle;
      const live = this.npcAlive.get(id) ?? true;
      const m = this.tp.sync(inst, w, live);
      if (m) this.remoteMuzzle.set(`n${id}`, m);
      const fl = this.npcFlags.get(id) ?? 0;
      if ((fl & WeaponFlag.Burning) && live) this.fx.burning(inst.x, inst.y, inst.z);
    }
    for (const d of this.debugDummies) {
      alive.add(d.inst);
      host.renderer.updateMeshInstancePose(d.inst, dtSec);
      this.tp.sync(d.inst, d.weapon, true);
    }
    this.tp.prune(alive);
    this.floor.update(host.pickups(), dtSec, host.localState(), host.lite());
    this.drawProjectiles(nowMs, dtSec);
    this.fx.frame(dtSec);
    // Свой непрерывный звук (пила на холостых тоже урчит).
    if (host.controlActive() && !host.thirdPerson() && this.state.active === WeaponId.Chainsaw) {
      const def = weaponDef(WeaponId.Chainsaw);
      const firing = this.fireHeldLocal && this.state.hasAmmo(WeaponId.Chainsaw);
      this.fx.keepLoop("self", def.sound, (firing ? def.soundVol : def.soundVol * 0.35) * host.soundVolume(), firing ? 1.15 : 0.8);
    }
    if (this.fireHeldLocal && this.state.active === WeaponId.Flamethrower && nowMs - this.lastFireLocalMs < 120) {
      this.fx.keepLoop("self", weaponDef(WeaponId.Flamethrower).sound, 0.45 * host.soundVolume());
    }
    // HUD.
    const show = host.inGame() && myId >= 0;
    this.hud.setVisible(show);
    if (show) this.hud.update(this.state, nowMs);
  }

  /** Камера кадра: вид от первого лица, тряска от взрывов, приближение. */
  beforeRender(cam: { x: number; y: number; yaw: number; pitch: number; bobZ?: number; bobRoll?: number; eyeZ?: number }, dtSec: number): void {
    const host = this.host;
    // Тряска.
    const sh = this.fx.shake;
    if (sh > 0.001) {
      cam.yaw += (Math.random() - 0.5) * 0.03 * sh;
      cam.pitch += (Math.random() - 0.5) * 0.03 * sh;
      cam.eyeZ = (cam.eyeZ ?? 1.6) + (Math.random() - 0.5) * 0.05 * sh;
    }
    // Оптика снайперки: ПКМ (держать) или Z / кнопка на телефоне (переключатель).
    const sniper = this.state.active === WeaponId.Sniper;
    if (!sniper) this.zoomToggled = false;
    const canZoom = (this.zoomHeld || this.zoomToggled) && sniper && host.controlActive() && !host.thirdPerson();
    if (!canZoom && this.zoom < 0.05) this.zoomFov = ZOOM_FOV;
    this.zoom += ((canZoom ? 1 : 0) - this.zoom) * Math.min(1, dtSec * 14);
    if (this.zoom < 0.001) this.zoom = 0;
    const fov = BASE_FOV + (this.zoomFov - BASE_FOV) * this.zoom;
    host.renderer.setFovY(fov);
    // Мышь в прицеле — пропорционально углу обзора (на ×7,5 иначе не прицелиться).
    host.input.lookScale = Math.max(0.12, fov / BASE_FOV);
    this.hud.setScope(this.zoom > 0.85);
    this.hud.setScopeButton(sniper, canZoom);

    const fp = !host.thirdPerson() && host.controlActive() && host.inGame() && !host.fpVideo && this.zoom < 0.85;
    const ls = host.localState();
    const eyeZ = (cam.eyeZ ?? 1.6) + (cam.bobZ ?? 0);
    this.vm.setFiringHeld(this.fireHeldLocal && this.state.hasAmmo(this.state.active), dtSec);
    this.vm.update(fp, { x: cam.x, y: cam.y, z: eyeZ, yaw: cam.yaw, pitch: cam.pitch, roll: cam.bobRoll ?? 0 },
      ls.speed, ls.grounded, dtSec, this.state.ammoOf(this.state.active));
    if (fp && this.vm.muzzleValid) this.ownMuzzle = this.vm.muzzle;
    else if (!host.thirdPerson()) this.ownMuzzle = null;
  }

  appendLights(out: PointLight[]): void {
    this.fx.appendLights(out);
    this.floor.appendLights(out);
  }

  /** Оружие на полу рисует этот модуль (общий код pickup'ов его пропускает). */
  isWeaponPickup(type: number): boolean {
    return WeaponPickups.isWeapon(type);
  }

  /** Патроны активного оружия и вместимость (для большого счётчика AMMO). */
  activeAmmo(): number {
    return this.state.ammoOf(this.state.active);
  }

  activeMaxAmmo(): number {
    return weaponDef(this.state.active).maxAmmo;
  }

  activeTitle(): string {
    return t(weaponDef(this.state.active).title);
  }

  /** Новая сессия / рестарт: всё в исходное. */
  reset(): void {
    this.state.reset();
    this.vm.setWeapon(this.state.active);
    this.tp.clear();
    this.floor.clear();
    for (const v of this.projectiles.values()) if (v.inst) this.host.renderer.removeMeshInstance(v.inst);
    this.projectiles.clear();
    this.playerWeapon.clear();
    this.playerFlags.clear();
    this.npcWeapon.clear();
    this.npcFlags.clear();
    this.npcAlive.clear();
    this.fx.stopAll();
    this.zoomHeld = false;
    this.zoomToggled = false;
  }
}

